import React from 'react';
import { Ruler, Info } from 'lucide-react';
import DynamicPageSections from '../components/site/DynamicPageSections';
import { useSiteStructureSections } from '../hooks/useSiteStructureSections';

const SizeGuideHeroStatic: React.FC = () => (
  <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16 md:py-20">
    <div className="container mx-auto px-4">
      <div className="max-w-3xl mx-auto text-center">
        <Ruler className="w-16 h-16 mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Size Guide</h1>
        <p className="text-xl text-blue-100">Find your perfect fit with our comprehensive size charts</p>
      </div>
    </div>
  </section>
);

const SizeCharts: React.FC = () => (
  <section className="py-16">
    <div className="container mx-auto px-4">
      <div className="max-w-4xl mx-auto space-y-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900">Clothing Sizes</h2>
            <p className="text-gray-600 mt-2">All measurements are in inches</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-gray-900">Size</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">Chest</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">Waist</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">Hips</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">US</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">UK</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">EU</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-6 py-4 font-medium">XS</td>
                  <td className="px-6 py-4 text-center">32-34</td>
                  <td className="px-6 py-4 text-center">24-26</td>
                  <td className="px-6 py-4 text-center">34-36</td>
                  <td className="px-6 py-4 text-center">0-2</td>
                  <td className="px-6 py-4 text-center">4-6</td>
                  <td className="px-6 py-4 text-center">32-34</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">S</td>
                  <td className="px-6 py-4 text-center">34-36</td>
                  <td className="px-6 py-4 text-center">26-28</td>
                  <td className="px-6 py-4 text-center">36-38</td>
                  <td className="px-6 py-4 text-center">4-6</td>
                  <td className="px-6 py-4 text-center">8-10</td>
                  <td className="px-6 py-4 text-center">36-38</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">M</td>
                  <td className="px-6 py-4 text-center">36-38</td>
                  <td className="px-6 py-4 text-center">28-30</td>
                  <td className="px-6 py-4 text-center">38-40</td>
                  <td className="px-6 py-4 text-center">8-10</td>
                  <td className="px-6 py-4 text-center">12-14</td>
                  <td className="px-6 py-4 text-center">40-42</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">L</td>
                  <td className="px-6 py-4 text-center">38-40</td>
                  <td className="px-6 py-4 text-center">30-32</td>
                  <td className="px-6 py-4 text-center">40-42</td>
                  <td className="px-6 py-4 text-center">12-14</td>
                  <td className="px-6 py-4 text-center">16-18</td>
                  <td className="px-6 py-4 text-center">44-46</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">XL</td>
                  <td className="px-6 py-4 text-center">40-42</td>
                  <td className="px-6 py-4 text-center">32-34</td>
                  <td className="px-6 py-4 text-center">42-44</td>
                  <td className="px-6 py-4 text-center">16-18</td>
                  <td className="px-6 py-4 text-center">20-22</td>
                  <td className="px-6 py-4 text-center">48-50</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">XXL</td>
                  <td className="px-6 py-4 text-center">42-44</td>
                  <td className="px-6 py-4 text-center">34-36</td>
                  <td className="px-6 py-4 text-center">44-46</td>
                  <td className="px-6 py-4 text-center">20-22</td>
                  <td className="px-6 py-4 text-center">24-26</td>
                  <td className="px-6 py-4 text-center">52-54</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900">Shoe Sizes</h2>
            <p className="text-gray-600 mt-2">Foot length in centimeters</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-gray-900">US</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">UK</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">EU</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-900">CM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-6 py-4">5</td>
                  <td className="px-6 py-4 text-center">3</td>
                  <td className="px-6 py-4 text-center">35-36</td>
                  <td className="px-6 py-4 text-center">22</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">6</td>
                  <td className="px-6 py-4 text-center">4</td>
                  <td className="px-6 py-4 text-center">36-37</td>
                  <td className="px-6 py-4 text-center">23</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">7</td>
                  <td className="px-6 py-4 text-center">5</td>
                  <td className="px-6 py-4 text-center">37-38</td>
                  <td className="px-6 py-4 text-center">23.5</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">8</td>
                  <td className="px-6 py-4 text-center">6</td>
                  <td className="px-6 py-4 text-center">38-39</td>
                  <td className="px-6 py-4 text-center">24</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">9</td>
                  <td className="px-6 py-4 text-center">7</td>
                  <td className="px-6 py-4 text-center">39-40</td>
                  <td className="px-6 py-4 text-center">25</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">10</td>
                  <td className="px-6 py-4 text-center">8</td>
                  <td className="px-6 py-4 text-center">40-41</td>
                  <td className="px-6 py-4 text-center">26</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">11</td>
                  <td className="px-6 py-4 text-center">9</td>
                  <td className="px-6 py-4 text-center">41-42</td>
                  <td className="px-6 py-4 text-center">27</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-blue-50 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <Info className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">How to Measure</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Chest</h3>
              <p className="text-gray-600 text-sm">
                Measure around the fullest part of your chest, keeping the tape horizontal.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Waist</h3>
              <p className="text-gray-600 text-sm">
                Measure around your natural waistline, the narrowest part of your torso.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Hips</h3>
              <p className="text-gray-600 text-sm">
                Measure around the fullest part of your hips, about 8 inches below your waist.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SizeGuide: React.FC = () => {
  const { sections, ready } = useSiteStructureSections('/size-guide');
  const showCmsHeader = ready && sections.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16">
      <main className="flex-grow">
        {showCmsHeader ? <DynamicPageSections sections={sections} /> : <SizeGuideHeroStatic />}
        <SizeCharts />
      </main>
    </div>
  );
};

export default SizeGuide;
